<template>
  <section class="bet-limit-settings" :aria-busy="busy">
    <h3>{{ memberId ? '회원별 베팅·당첨 한도' : '사이트 공통 베팅·당첨 한도' }}</h3>
    <p class="limit-help">금액은 원 단위이며 0은 무제한입니다. {{ memberId ? '공통값 사용을 선택하면 해당 항목의 사이트 설정을 따릅니다.' : '카지노·슬롯별 공통 한도입니다. 기존 업체별·등급별 설정과 별도로 관리합니다.' }}</p>
    <p class="limit-help">요청 1건 기준으로 적용되며, 최대 당첨금은 베팅금을 포함한 총 지급액 기준입니다.</p>
    <p v-if="loading" role="status">한도를 불러오는 중입니다.</p>
    <p v-if="error" role="alert" class="limit-error">{{ error }}</p>
    <p v-if="notice" role="status">{{ notice }}</p>
    <button type="button" class="limit-button" :disabled="busy" @click="load">다시 불러오기</button>
    <fieldset v-for="row in rows" :key="row.category" :disabled="busy" class="limit-row">
      <legend>{{ categoryName(row.category) }}</legend>
      <div class="limit-fields">
        <div v-for="field in fields" :key="field.key" class="limit-field">
          <label>
            {{ field.label }}
            <input :value="row[field.key]" @input="formatInput($event, row, field.key)" type="text" inputmode="numeric" autocomplete="off"
              :disabled="!!memberId && row.inherit[field.key]" placeholder="금액 입력 (0 = 무제한)" />
          </label>
          <label v-if="memberId" class="limit-inherit">
            <input v-model="row.inherit[field.key]" type="checkbox" /> 공통값 사용
          </label>
          <p v-if="memberId" class="limit-help">사이트 공통: {{ displayAmount(row.siteSetting?.[field.key]) }}</p>
          <p class="limit-help">현재 적용: {{ displayAmount(row.effective?.[field.key]) }} · {{ sourceName(row.effective?.source?.[field.key]) }}</p>
        </div>
      </div>
      <div class="limit-actions">
        <button type="button" class="limit-button primary" @click="save(row)">{{ categoryName(row.category) }} 한도 저장</button>
        <button v-if="memberId" type="button" class="limit-button" @click="reset(row)">회원 예외 해제</button>
      </div>
    </fieldset>
  </section>
</template>

<script>
import { getSiteBetLimit, saveSiteBetLimit, getMemberBetLimit, saveMemberBetLimit, resetMemberBetLimit } from '@/api/setting'

const fields = [
  { key: 'betAmtMax', label: '최대 베팅금' },
  { key: 'betWinAmtMax', label: '최대 당첨금' }
]
const errors = {
  '9002': '입력 형식 또는 금액 범위를 확인해주세요.',
  '9004': '사이트 또는 회원을 찾을 수 없습니다.',
  '1000': '한도 설정 권한이 없습니다.',
  TK999: '로그인 정보가 변경되었습니다. 다시 로그인해주세요.',
  '9999': '인증 또는 서버 오류가 발생했습니다. 다시 로그인하거나 잠시 후 재시도해주세요.'
}

export default {
  name: 'BetLimitSettings',
  props: {
    category: { type: String, default: '' },
    memberId: { type: String, default: '' }
  },
  data () {
    return { fields, rows: [], loading: false, saving: false, error: '', notice: '' }
  },
  computed: {
    busy () { return this.loading || this.saving }
  },
  methods: {
    formatInputAmount (value) {
      if (value == null) return ''
      const digits = String(value).replace(/,/g, '')
      return /^\d*$/.test(digits) ? digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : String(value)
    },
    formatInput (event, row, key) {
      const input = event.target
      const position = input.selectionStart
      const before = input.value.slice(0, position).replace(/,/g, '').length
      const formatted = this.formatInputAmount(input.value)
      row[key] = formatted
      if (formatted !== input.value) {
        input.value = formatted
        if (position !== null) {
          let caret = 0
          let digits = 0
          while (caret < formatted.length && digits < before) {
            if (formatted[caret] !== ',') digits++
            caret++
          }
          input.setSelectionRange(caret, caret)
        }
      }
    },
    categoryName (category) { return category === 'casino' ? '카지노' : '슬롯' },
    sourceName (source) { return { SITE: '사이트 공통', MEMBER: '회원 개별', UNSET: '미설정' }[source] || '미설정' },
    displayAmount (value) {
      if (value === null || value === undefined) return '미설정'
      if (String(value) === '0') return '무제한'
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '원'
    },
    readRows (response, categories) {
      if (response?.resultCode !== '0') throw new Error(errors[response?.resultCode] || response?.resultMessage || '한도 요청에 실패했습니다.')
      const list = response.data?.list
      if (!Array.isArray(list)) throw new Error('한도 응답을 확인하지 못했습니다. 다시 불러와주세요.')
      return categories.map(category => {
        const item = list.find(item => item?.category === category)
        if (!item) throw new Error('한도 응답에 요청한 카테고리가 없습니다. 다시 불러와주세요.')
        const setting = this.memberId ? item.memberSetting : item.setting
        const row = { category, effective: item.effective, siteSetting: item.siteSetting, inherit: {} }
        for (const { key } of fields) {
          row[key] = this.formatInputAmount(setting?.[key])
          row.inherit[key] = setting?.[key] == null
        }
        return row
      })
    },
    failureMessage (error) {
      if ([401, 422].includes(error.response?.status)) return '로그인이 만료되었습니다. 다시 로그인해주세요.'
      return error.response || error.code || error.message === 'No token'
        ? '한도를 조회하거나 저장하지 못했습니다. 로그인 상태와 연결을 확인하고 다시 불러와주세요.'
        : error.message || '한도 처리 중 오류가 발생했습니다.'
    },
    async load () {
      if (this.busy) return
      this.loading = true
      this.error = ''
      this.notice = ''
      this.rows = []
      try {
        const params = this.category ? { category: this.category } : {}
        if (this.memberId) params.memId = this.memberId
        const response = await (this.memberId ? getMemberBetLimit(params) : getSiteBetLimit(params))
        this.rows = this.readRows(response, this.category ? [this.category] : ['casino', 'slot'])
      } catch (error) {
        this.error = this.failureMessage(error)
      } finally {
        this.loading = false
      }
    },
    async save (row) {
      if (this.busy) return
      this.error = ''
      this.notice = ''
      const params = { category: row.category }
      if (this.memberId) params.memId = this.memberId
      for (const { key, label } of fields) {
        if (this.memberId && row.inherit[key]) {
          params[key] = null
        } else {
          const value = row[key].trim().replace(/,/g, '')
          if (!/^\d+$/.test(value)) {
            this.error = label + '을 0 이상의 정수로 입력해주세요. 0은 무제한입니다.'
            return
          }
          params[key] = value.replace(/^0+(?=\d)/, '')
        }
      }
      if (!confirm(this.categoryName(row.category) + ' 한도를 저장하시겠습니까? 0은 무제한이며 회원 개별 설정이 있으면 공통값보다 우선합니다.')) return
      this.saving = true
      try {
        const response = await (this.memberId ? saveMemberBetLimit(params) : saveSiteBetLimit(params))
        const [updated] = this.readRows(response, [row.category])
        this.rows = this.rows.map(item => item.category === row.category ? updated : item)
        this.notice = this.categoryName(row.category) + ' 한도를 저장했습니다.'
      } catch (error) {
        this.error = this.failureMessage(error)
      } finally {
        this.saving = false
      }
    },
    async reset (row) {
      if (this.busy || !this.memberId) return
      if (!confirm(this.categoryName(row.category) + ' 회원 예외를 해제하고 두 항목 모두 사이트 공통값을 사용하시겠습니까?')) return
      this.saving = true
      this.error = ''
      this.notice = ''
      try {
        const response = await resetMemberBetLimit({ memId: this.memberId, category: row.category })
        const [updated] = this.readRows(response, [row.category])
        this.rows = this.rows.map(item => item.category === row.category ? updated : item)
        this.notice = '회원 예외를 해제했습니다. 사이트 공통값이 적용됩니다.'
      } catch (error) {
        this.error = this.failureMessage(error)
      } finally {
        this.saving = false
      }
    }
  },
  created () { this.load() }
}
</script>

<style scoped>
.bet-limit-settings {margin: 18px 0;padding: 18px;background: #1f2937;border: 1px solid #313a49;border-radius: 5px;color: #d1d5db;}
h3 {font-size: 16px;margin-bottom: 10px;}
.limit-help {font-size: 12px;line-height: 1.6;margin: 6px 0;color: #cbd5e1;}
.limit-error {color: #fca5a5;line-height: 1.6;}
.limit-row {border: 1px solid #465164;padding: 16px;margin-top: 16px;min-width: 0;}
legend {padding: 0 8px;font-weight: 600;}
.limit-fields {display: flex;flex-wrap: wrap;gap: 24px;}
.limit-field {flex: 1;min-width: 220px;}
.limit-field label {display: block;font-size: 13px;}
.limit-field input[type=text] {display: block;width: 100%;box-sizing: border-box;margin-top: 8px;padding: 8px;background: #111827;color: #fff;border: 1px solid #64748b;}
.limit-field input:disabled {opacity: .5;}
.limit-inherit {margin: 10px 0;}
.limit-inherit input {appearance: auto;margin-right: 5px;}
.limit-actions {display: flex;gap: 10px;margin-top: 14px;}
.limit-button {padding: 8px 12px;background: #374151;color: #fff;border-radius: 4px;cursor: pointer;font: inherit;}
.limit-button.primary {background: #13764b;}
fieldset:disabled .limit-button, .limit-button:disabled {opacity: .5;cursor: wait;}
.limit-button:focus-visible, input:focus-visible {outline: 2px solid #93c5fd;outline-offset: 2px;}
</style>
